import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Switch, TableSkeleton, Tabs,
} from '../components/ui';
import usePhoneCalls from '../utils/usePhoneCalls';

/* ── Voice ───────────────────────────────────────────────────────────────────
 * Speech models, of three kinds (backend/api/routes/voice_models.py, providers in
 * proxy/shared/speech.py):
 *   - text to speech (T2S): behind the agent's `tts` tool, e.g. Kokoro, Edge TTS, an
 *     OpenAI-compatible speech API or ElevenLabs, whose voices are its account's own (the
 *     backend lists them with the row's key: `account_voices`);
 *   - speech to text (S2T): behind the `stt` tool, e.g. Whisper;
 *   - speech to speech (S2S): stored for the voice channel, nothing calls them yet.
 * The Tools page picks the S2T model the `stt` tool uses, and lists the languages the `tts`
 * tool speaks, each with the T2S model and the voice of that language that speak it. So a
 * T2S row has no voice of its own here, only a speed. Test calls the model directly, as the
 * Models page does: S2T transcribes a bundled sample, T2S speaks the text typed in the dialog
 * with the voice picked there, and plays it back. The key is write-only.
 * A T2S or S2T row has a price per second of audio: proxy-llm bills each call the seconds it
 * spoke or heard at that price, on the user's budget like an LLM call, and the Dashboard
 * shows the spend in its Voice section. */

const EMPTY_FORM = {
  voice_key: '', ui_name: '', kind: 't2s', provider: 'kokoro', endpoint: '', model_id: '',
  deployment: '', api_version: '', voice: '', speed: '', price_per_second: '',
  api_key: '', active: true, sort_order: 0,
};

// Per second of audio, in the Models page's currency. Per-second prices are small, so more
// decimals than the Models page's per-1M-token figures.
const priceFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 8,
});
const formatPrice = (value) => {
  const n = Number(value) || 0;
  return n > 0 ? priceFormatter.format(n) : <span className="text-faint">{priceFormatter.format(0)}</span>;
};

const EDGE_ENDPOINT = 'wss://speech.platform.bing.com/consumer/speech/synthesize/readaloud/edge/v1';
const HINTS = {
  kokoro: { endpoint: 'wss://spod.ai-factory.fastweb.it/tts/streaming' },
  'openai-whisper': {
    endpoint: 'https://bpod1.ai-factory.fastweb.it/v1/audio/transcriptions',
    model_id: 'Empty: openai/whisper-large-v3-turbo on the AI Factory, whisper-1 elsewhere.',
  },
  'realtime-stt': {
    endpoint: 'wss://bpod1.ai-factory.fastweb.it/ws/transcribe',
    endpointHint: 'Phone calls listen through it (Tiers, Phone calls). The AI Factory\'s, or ElevenLabs\' wss://api.elevenlabs.io/v1/speech-to-text/realtime.',
    model_id: 'Empty: none on the AI Factory, scribe_v2_realtime on ElevenLabs.',
  },
  'edge-tts': { endpoint: EDGE_ENDPOINT, endpointHint: 'Microsoft\'s, which edge-tts uses. Empty: the same.' },
  'openai-speech': {
    endpoint: 'https://api.openai.com/v1',
    model_id: 'Empty: tts-1. A base URL gets /v1/audio/speech.',
  },
  elevenlabs: {
    endpoint: 'https://api.elevenlabs.io',
    endpointHint: 'ElevenLabs\' API. An account with EU data residency uses https://api.eu.residency.elevenlabs.io.',
    model_id: 'Empty: eleven_multilingual_v2. Also eleven_flash_v2_5 or eleven_v3.',
    speed: '0.7 to 1.2, the speeds ElevenLabs takes. Empty: 1.',
  },
  'azure-openai-realtime': {
    endpoint: 'wss://<resource>.openai.azure.com/openai/realtime',
    deployment: 'The deployment name, e.g. gpt-realtime.', api_version: '2025-04-01-preview',
  },
  'openai-realtime': { endpoint: 'wss://api.openai.com/v1/realtime', deployment: 'The model, e.g. gpt-realtime.' },
  other: { endpoint: 'https://host/…', deployment: 'The model or deployment name.' },
};

const KIND_TONE = { t2s: 'accent', s2t: 'info', s2s: 'violet' };
// A sentence per language for the T2S test, by the language's base code.
const DEFAULT_TEST_TEXT = {
  it: 'Ciao, mi chiamo Test. Come posso aiutarti?',
  en: 'Hello, my name is Test. How can I help you?',
  de: 'Hallo, ich heiße Test. Wie kann ich dir helfen?',
  fr: "Bonjour, je m'appelle Test. Comment puis-je t'aider ?",
  es: 'Hola, me llamo Test. ¿Cómo puedo ayudarte?',
  pt: 'Olá, meu nome é Test. Como posso ajudar?',
  ro: 'Bună, mă numesc Test. Cu ce te pot ajuta?',
};
const DASH = <span className="text-faint">—</span>;
const shortUrl = (url) => (url || '').replace(/^(https?|wss?):\/\//, '');
// The test's sentence for a voice's language; Italian for a voice that speaks any, or none
// picked (the server then speaks with the Tools page's voice for the model).
const testTextFor = (language) => DEFAULT_TEST_TEXT[language || 'it'] ?? '';
// A provider's voices grouped by the language each speaks, by language name, for the test's
// picker. Voices that speak every language (the speech API's) make one group of their own.
const voiceGroups = (voices) => {
  const groups = new Map();
  (voices || []).forEach((v) => {
    const name = v.language_label || 'Every language';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(v);
  });
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
};

/* ── Test result ─────────────────────────────────────────────────── */
function VoiceTestResult({ result: r }) {
  const ok = !!r.success;
  const raw = r.raw_response !== undefined ? r.raw_response : r;
  const audioSrc = r.audio_base64
    ? `data:${r.format === 'wav' ? 'audio/wav' : 'audio/mpeg'};base64,${r.audio_base64}` : null;
  return (
    <div className="stack gap-3">
      <div className={`notice ${ok ? 'notice-ok' : 'notice-danger'} mb-0`}>
        <Icon name={ok ? 'checkCircle' : 'xCircle'} />
        <div className="notice-body">
          <div className="row-wrap gap-3">
            <span className="notice-title">{ok ? 'Success' : 'Failed'}</span>
            {r.status_code != null && r.status_code !== 0 && <span className="num">HTTP {r.status_code}</span>}
            {r.elapsed_seconds != null && <span className="num">{r.elapsed_seconds}s</span>}
            {r.duration_seconds != null && <span className="num">{r.duration_seconds}s of audio</span>}
            {r.size_bytes != null && <span className="num">{Math.round(r.size_bytes / 1024)} KB {r.format}</span>}
            {r.voice && (
              <span>Voice {r.voice_label || <span className="mono">{r.voice}</span>}</span>
            )}
            {r.language && !r.voice_label && <span>Language <span className="mono">{r.language}</span></span>}
          </div>
          {r.detail && (
            <div className="mt-1">{typeof r.detail === 'string' ? r.detail : JSON.stringify(r.detail)}</div>
          )}
        </div>
      </div>
      {audioSrc && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- synthesised from the text above
        <audio controls autoPlay src={audioSrc} style={{ width: '100%' }} aria-label="Synthesised audio" />
      )}
      {r.text != null && (
        <div className="stack gap-1">
          <span className="text-sm text-muted">Transcript</span>
          <div className="card-inset pre-wrap">{r.text || <span className="text-faint">(empty)</span>}</div>
        </div>
      )}
      {r.expected && (
        <div className="stack gap-1">
          <span className="text-sm text-muted">What the sample says</span>
          <div className="card-inset pre-wrap text-muted">{r.expected}</div>
        </div>
      )}
      {raw != null && raw !== '' && (
        <details>
          <summary className="text-sm text-muted">Raw response</summary>
          <pre className="code-block mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
            {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

/* ── The S2T samples, each playable ─────────────────────────────── */
function SamplePicker({ samples, value, onChange }) {
  const [urls, setUrls] = useState({});
  const urlsRef = useRef({});
  useEffect(() => () => Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u)), []);
  const load = async (id) => {
    if (urlsRef.current[id]) return;
    try {
      const resp = await apiFetch(`/api/voice-models/samples/${id}`);
      if (!resp.ok) return;
      const url = URL.createObjectURL(await resp.blob());
      urlsRef.current = { ...urlsRef.current, [id]: url };
      setUrls(urlsRef.current);
    } catch { /* the sample just cannot be previewed */ }
  };
  return (
    <fieldset className="stack gap-2">
      <legend className="form-label">Sample</legend>
      {samples.map((s) => (
        <div key={s.id} className="card-inset stack gap-2">
          <label className="row gap-2" htmlFor={`voice-sample-${s.id}`}>
            <input type="radio" id={`voice-sample-${s.id}`} name="voice-sample" value={s.id}
                   checked={value === s.id} onChange={() => onChange(s.id)} />
            <span className="cell-primary">{s.label}</span>
            <span className="text-sm text-muted">{s.text}</span>
          </label>
          {urls[s.id] ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- the transcript is the label above
            <audio controls src={urls[s.id]} style={{ width: '100%' }} aria-label={`${s.label} sample`} />
          ) : (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => load(s.id)}>
              <Icon name="play" /> Listen to the sample
            </button>
          )}
        </div>
      ))}
    </fieldset>
  );
}

function Voice() {
  const [models, setModels] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [providers, setProviders] = useState([]);
  // Each T2S provider's voices with the language each speaks: the Test's picker.
  const [voices, setVoices] = useState({});
  // An ElevenLabs row's picker instead, by row key: {voices, error} from its account.
  const [accountVoices, setAccountVoices] = useState({});
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testKey, setTestKey] = useState(null);
  const [testStates, setTestStates] = useState({});
  const [confirm, confirmDialog] = useConfirm();

  const reload = async () => {
    try {
      const resp = await apiFetch('/api/voice-models');
      if (resp.ok) {
        const data = await resp.json();
        setModels(data.models || []);
        setKinds(data.kinds || []);
        setProviders(data.providers || []);
        setVoices(data.voices || {});
        setAccountVoices(data.account_voices || {});
        setLoadError(false);
      } else setLoadError(true);
    } catch (e) { setLoadError(true); }
  };

  useEffect(() => {
    (async () => {
      await reload();
      try {
        const resp = await apiFetch('/api/voice-models/samples');
        if (resp.ok) setSamples((await resp.json()).samples || []);
      } catch { /* the S2T test offers no samples then */ }
      setLoading(false);
    })();
  }, []);

  // A realtime speech-to-text model serves phone calls only, so where the environment takes
  // none (voice_channel_enabled) it is not offered, except to a row that already is one.
  const phoneOn = usePhoneCalls();
  const offered = (p, current) => p.value !== 'realtime-stt' || phoneOn !== false
    || p.value === current;
  const providerSpec = (value) => providers.find((p) => p.value === value) || null;
  const spec = providerSpec(form.provider);
  const takes = (setting) => (spec?.settings || []).includes(setting);
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const changeKind = (kind) => setForm((f) => {
    const first = providers.find((p) => p.kind === kind && offered(p, null));
    return { ...f, kind, provider: first ? first.value : f.provider, voice: '' };
  });
  // A voice is one provider's name: another provider starts without it.
  const changeProvider = (provider) => setForm((f) => (
    f.provider === provider ? f : { ...f, provider, voice: '' }));

  const openCreate = () => {
    const kind = kindFilter !== 'all' ? kindFilter : 't2s';
    const first = providers.find((p) => p.kind === kind && offered(p, null));
    setForm({ ...EMPTY_FORM, kind, provider: first ? first.value : EMPTY_FORM.provider });
    setEditingKey(null);
    setShowForm(true);
  };
  const handleEdit = (m) => {
    const s = m.settings || {};
    setForm({
      voice_key: m.voice_key, ui_name: m.ui_name, kind: m.kind, provider: m.provider,
      endpoint: m.endpoint || '', model_id: m.model_id || '', deployment: m.deployment || '',
      api_version: m.api_version || '', voice: m.voice || '',
      speed: s.speed != null ? String(s.speed) : '',
      price_per_second: m.price_per_second ? String(m.price_per_second) : '',
      api_key: '', active: !!m.active, sort_order: m.sort_order || 0,
      _api_key_set: !!m.api_key_set, _api_key_display: m.api_key_display || '',
    });
    setEditingKey(m.voice_key);
    setShowForm(true);
  };
  const closeForm = () => { if (!saving) { setShowForm(false); setEditingKey(null); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const takesEndpoint = (spec?.endpoint || []).length > 0;
    // A provider with a default endpoint (Edge TTS) takes an empty one as that default.
    const needsEndpoint = takesEndpoint && !spec?.default_endpoint;
    if (!form.ui_name.trim() || (!editingKey && !form.voice_key.trim())
        || (needsEndpoint && !form.endpoint.trim())) {
      toast.error(needsEndpoint ? 'Name, key and endpoint are required' : 'Name and key are required');
      return;
    }
    const settings = {};
    if (takes('speed') && String(form.speed).trim()) settings.speed = Number(form.speed);
    // The backend refuses a row with no key for a provider whose calls need one; said here
    // first, with the field it is about.
    if (spec?.needs_key && !form.api_key.trim() && !(editingKey && form._api_key_set)) {
      toast.error(`${spec.label} needs an API key`);
      return;
    }
    const payload = {
      ui_name: form.ui_name.trim(), provider: form.provider,
      endpoint: takesEndpoint ? (spec?.default_endpoint || form.endpoint.trim()) : '',
      model_id: spec?.model_id ? form.model_id : '', voice: spec?.voice ? form.voice : '',
      deployment: spec?.deployment ? form.deployment : '', api_version: spec?.deployment ? form.api_version : '',
      settings, active: form.active, sort_order: parseInt(form.sort_order, 10) || 0,
    };
    if (!editingKey) { payload.voice_key = form.voice_key.trim(); payload.kind = form.kind; }
    if (form.api_key.trim()) payload.api_key = form.api_key.trim();
    if (form.kind !== 's2s') {
      const price = String(form.price_per_second).trim();
      const value = price ? Number(price) : 0;
      if (!Number.isFinite(value) || value < 0) {
        toast.error('The price per second must be a number, 0 or more');
        return;
      }
      payload.price_per_second = value;
    }
    setSaving(true);
    try {
      const resp = await apiFetch(editingKey ? `/api/voice-models/${editingKey}` : '/api/voice-models', {
        method: editingKey ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        toast.success(editingKey ? 'Voice model updated' : 'Voice model added');
        setShowForm(false);
        setEditingKey(null);
        reload();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to save the voice model');
      }
    } catch { toast.error('Failed to save the voice model'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (m) => {
    if (!await confirm({
      title: `Delete voice model ${m.ui_name}?`,
      body: <p>Its endpoint and key are removed.</p>,
      confirmLabel: 'Delete voice model',
    })) return;
    try {
      const resp = await apiFetch(`/api/voice-models/${m.voice_key}`, { method: 'DELETE' });
      if (resp.ok) { toast.success('Voice model deleted'); reload(); }
      else { const err = await resp.json().catch(() => ({})); toast.error(err.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  /* ── Test ─────────────────────────────────────────────────────── */
  const testModel = testKey ? models.find((m) => m.voice_key === testKey) : null;
  const getTestState = (m) => testStates[m.voice_key]
    || { text: testTextFor(), voice: '', sample: samples[0]?.id || 'it', loading: false, result: null };
  // The voices a row's test offers: its account's for an ElevenLabs row, else its provider's.
  const testVoices = (m) => (accountVoices[m.voice_key] ? accountVoices[m.voice_key].voices
    : voices[m.provider]) || [];
  const voiceLanguage = (m, voice) => (testVoices(m).find((v) => v.value === voice) || {}).language;
  const changeTestVoice = (m, voice) => {
    const state = getTestState(m);
    // A text the admin typed stays; the sample sentence follows the voice's language.
    const untouched = state.text === testTextFor(voiceLanguage(m, state.voice));
    setTest(m, { voice, result: null,
      text: untouched ? (testTextFor(voiceLanguage(m, voice)) || state.text) : state.text });
  };
  const testState = testModel ? getTestState(testModel) : null;
  // An ElevenLabs row's account listing ({voices, error}), or undefined for any other row.
  const testAccount = testModel ? accountVoices[testModel.voice_key] : undefined;
  const setTest = (m, patch) => setTestStates((prev) => ({ ...prev, [m.voice_key]: { ...getTestState(m), ...patch } }));
  const runTest = async (m) => {
    const state = getTestState(m);
    setTest(m, { loading: true, result: null });
    try {
      const resp = await apiFetch(`/api/voice-models/${m.voice_key}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(m.kind === 's2t' ? { sample: state.sample }
          : { text: state.text, voice: state.voice || undefined }),
      });
      const data = await resp.json();
      setTestStates((prev) => ({ ...prev, [m.voice_key]: { ...state, loading: false, result: data } }));
    } catch (err) {
      setTestStates((prev) => ({
        ...prev,
        [m.voice_key]: { ...state, loading: false, result: { success: false, status_code: 0, elapsed_seconds: 0, detail: err.message } },
      }));
    }
  };

  const counts = Object.fromEntries((kinds || []).map((k) => [k.value, models.filter((m) => m.kind === k.value).length]));
  const tabs = [{ value: 'all', label: 'All', count: models.length },
    ...kinds.map((k) => ({ value: k.value, label: k.label, count: counts[k.value] || 0 }))];
  const visible = kindFilter === 'all' ? models : models.filter((m) => m.kind === kindFilter);
  const hints = HINTS[form.provider] || HINTS.other;
  const kindProviders = providers.filter((p) => p.kind === form.kind
    && offered(p, editingKey ? (models.find((m) => m.voice_key === editingKey) || {}).provider : null));
  const addButton = (
    <button type="button" className="btn btn-primary" onClick={openCreate}>
      <Icon name="plus" /> Add voice model
    </button>
  );

  return (
    <div>
      <PageHeader description="Speech models: text to speech, speech to text and speech to speech."
                  actions={addButton}>
        <h1 className="page-title">
          Voice
          {!loading && <Badge tone="neutral" size="lg">{models.length}</Badge>}
        </h1>
      </PageHeader>

      {!loading && models.length > 0 && (
        <Tabs tabs={tabs} value={kindFilter} onChange={setKindFilter} variant="line" />
      )}

      <div className="card card-flush">
        {loading ? (
          <TableSkeleton rows={3} cols={5} label="Loading voice models" />
        ) : models.length === 0 && loadError ? (
          <EmptyState icon="alertCircle" title="Could not load voice models"
                      action={<button type="button" className="btn btn-secondary" onClick={reload}>Retry</button>}>
            The list did not load. Nothing has been changed.
          </EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState icon="voice" title="No voice models here yet" action={addButton}>
            Add a text-to-speech model such as Kokoro, a speech-to-text model such as Whisper, or a
            speech-to-speech model.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Kind</th>
                  <th>Provider</th>
                  <th>Endpoint</th>
                  <th>Model or voice</th>
                  <th className="num">Price / s</th>
                  <th>Status</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => {
                  const test = testStates[m.voice_key];
                  const s = providerSpec(m.provider);
                  return (
                    <tr key={m.voice_key}>
                      <td data-label="Model">
                        <div style={{ maxWidth: 220, minWidth: 0 }}>
                          <span className="cell-primary">{m.ui_name}</span>
                          <span className="cell-sub mono truncate" title={m.voice_key}>{m.voice_key}</span>
                        </div>
                      </td>
                      <td data-label="Kind"><Badge tone={KIND_TONE[m.kind] || 'neutral'}>{m.kind_label}</Badge></td>
                      <td data-label="Provider">{m.provider_label}</td>
                      <td data-label="Endpoint">
                        {m.endpoint ? (
                          <div className="mono text-sm truncate" style={{ maxWidth: 240 }} title={m.endpoint}>
                            {shortUrl(m.endpoint)}
                          </div>
                        ) : <span className="text-sm text-muted">Built in</span>}
                      </td>
                      <td data-label="Model or voice">
                        {m.model_id || m.deployment
                          ? <span className="mono text-sm">{m.model_id || m.deployment}</span> : (m.voice ? null : DASH)}
                        {m.voice && <span className={m.model_id || m.deployment ? 'cell-sub' : 'mono text-sm'}>
                          {m.model_id || m.deployment ? `voice ${m.voice}` : m.voice}</span>}
                      </td>
                      <td data-label="Price / s" className="num">
                        {m.kind === 's2s' ? DASH : formatPrice(m.price_per_second)}
                      </td>
                      <td data-label="Status">
                        <div className="status-lines">
                          <span className={m.active ? 'text-ok' : 'text-muted'}>
                            <span className="dot" aria-hidden="true" />{m.active ? 'Active' : 'Inactive'}
                          </span>
                          {m.in_use && ((m.languages || []).length > 0 || !(m.calls || []).length) && (
                            <span className="text-accent">
                              <span className="dot" aria-hidden="true" />
                              {/* A T2S row is in use for the languages the Tools page gives it. */}
                              {(m.languages || []).length ? `Speaks ${m.languages.join(', ')}` : 'In use'}
                            </span>
                          )}
                          {(m.calls || []).length > 0 && (
                            <span className="text-accent">
                              <span className="dot" aria-hidden="true" />
                              {`Phone calls: ${m.calls.join(', ')}`}
                            </span>
                          )}
                          {s?.needs_key && !m.api_key_set && (
                            <span className="text-warn"><span className="dot" aria-hidden="true" />No API key</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button type="button" className="btn btn-secondary btn-sm" disabled={m.kind === 's2s'}
                                  onClick={() => setTestKey(m.voice_key)} aria-label={`Test ${m.ui_name}`}
                                  title={m.kind === 's2s' ? 'Speech-to-speech models are not called yet' : 'Test'}>
                            {test?.loading ? <span className="spinner-sm" /> : <Icon name="play" />} Test
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => handleEdit(m)}
                                  aria-label={`Edit ${m.ui_name}`} title="Edit">
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn-danger-soft btn-sm btn-icon" onClick={() => handleDelete(m)}
                                  aria-label={`Delete ${m.ui_name}`} title="Delete voice model">
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={showForm} onClose={closeForm} maxWidth={720}
             title={editingKey ? `Edit ${form.ui_name || editingKey}` : 'Add voice model'}
             description={editingKey ? 'Changes apply at the next tool call.' : 'Each model carries its own endpoint and key.'}>
        <form onSubmit={handleSubmit}>
          <section aria-labelledby="voice-form-identity">
            <h2 id="voice-form-identity" className="section-title">Identity</h2>
            <div className="form-row">
              <Field label="Display name" htmlFor="voice-ui-name" required>
                <input id="voice-ui-name" className="form-input" value={form.ui_name}
                       onChange={(e) => update('ui_name', e.target.value)} placeholder="Kokoro" />
              </Field>
              <Field label="Key" htmlFor="voice-key" required
                     hint={editingKey ? 'The key cannot change.' : 'Lowercase, spaces become dashes.'}>
                <input id="voice-key" className="form-input mono" value={form.voice_key} disabled={!!editingKey}
                       spellCheck={false} autoComplete="off" placeholder="kokoro"
                       onChange={(e) => update('voice_key', e.target.value.toLowerCase().replace(/\s/g, '-'))} />
              </Field>
            </div>
            <div className="form-row">
              <Field label="Kind" htmlFor="voice-kind" required
                     hint={editingKey ? 'A model keeps its kind: a tool may be using it.' : null}>
                <select id="voice-kind" className="form-input" value={form.kind} disabled={!!editingKey}
                        onChange={(e) => changeKind(e.target.value)}>
                  {kinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Provider" htmlFor="voice-provider" required>
                <select id="voice-provider" className="form-input" value={form.provider}
                        onChange={(e) => changeProvider(e.target.value)}>
                  {kindProviders.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
            </div>
          </section>

          <section aria-labelledby="voice-form-endpoint" className="mt-4">
            <h2 id="voice-form-endpoint" className="section-title">Endpoint &amp; credentials</h2>
            {(spec?.endpoint || []).length > 0 && spec.default_endpoint ? (
              // Edge TTS has one endpoint, Microsoft's: shown, and not editable, because the
              // edge-tts library reads it from one setting for the whole service.
              <Field label="Endpoint URL" htmlFor="voice-endpoint"
                     hint="Microsoft's endpoint, the only one Edge TTS has.">
                <input id="voice-endpoint" className="form-input mono" value={spec.default_endpoint}
                       readOnly spellCheck={false} />
              </Field>
            ) : (spec?.endpoint || []).length > 0 ? (
              <Field label="Endpoint URL" htmlFor="voice-endpoint" required
                     hint={hints.endpointHint || `${spec.endpoint.join(' or ')}.`}>
                <input id="voice-endpoint" className="form-input mono" value={form.endpoint} inputMode="url"
                       spellCheck={false} autoComplete="off" placeholder={hints.endpoint}
                       onChange={(e) => update('endpoint', e.target.value)} />
              </Field>
            ) : (
              <p className="text-sm text-muted">This provider has no endpoint or key to set.</p>
            )}
            {spec && !spec.needs_key && (spec.endpoint || []).length > 0 && (
              <p className="text-sm text-muted">This provider takes no API key.</p>
            )}
            {spec?.model_id && (
              <Field label="Model ID" htmlFor="voice-model-id" hint={hints.model_id}>
                <input id="voice-model-id" className="form-input mono" value={form.model_id}
                       spellCheck={false} onChange={(e) => update('model_id', e.target.value)} />
              </Field>
            )}
            {spec?.deployment && (
              <div className="form-row">
                <Field label="Deployment or model" htmlFor="voice-deployment" hint={hints.deployment}>
                  <input id="voice-deployment" className="form-input mono" value={form.deployment}
                         spellCheck={false} onChange={(e) => update('deployment', e.target.value)} />
                </Field>
                <Field label="API version" htmlFor="voice-api-version" hint="Azure only.">
                  <input id="voice-api-version" className="form-input mono" value={form.api_version}
                         spellCheck={false} placeholder={hints.api_version}
                         onChange={(e) => update('api_version', e.target.value)} />
                </Field>
              </div>
            )}
            {spec?.needs_key && (
              <Field label="API key" htmlFor="voice-api-key" required={!(editingKey && form._api_key_set)}
                     hint={editingKey && form._api_key_set
                       ? <>Current key <code>{form._api_key_display}</code>. Leave blank to keep it.</>
                       : 'Write-only: after saving, only a masked preview is shown.'}>
                <input id="voice-api-key" className="form-input" type="password" autoComplete="new-password"
                       spellCheck={false} value={form.api_key}
                       placeholder={editingKey ? 'Leave blank to keep the current key' : 'Enter the API key…'}
                       onChange={(e) => update('api_key', e.target.value)} />
              </Field>
            )}
          </section>

          {(spec?.voice || takes('speed')) && (
            <section aria-labelledby="voice-form-voice" className="mt-4">
              <h2 id="voice-form-voice" className="section-title">{spec?.voice ? 'Voice' : 'Speech'}</h2>
              {form.kind === 't2s' && (
                <p className="text-sm text-muted">
                  The voice for each language this model speaks is picked on the Tools page, under
                  Voice, Text to speech.
                </p>
              )}
              <div className="form-row">
                {spec?.voice && (
                  <Field label="Voice" htmlFor="voice-voice" hint="The voice name the service takes, e.g. alloy.">
                    <input id="voice-voice" className="form-input mono" value={form.voice} spellCheck={false}
                           placeholder="alloy" onChange={(e) => update('voice', e.target.value)} />
                  </Field>
                )}
                {takes('speed') && (
                  <Field label="Speed" htmlFor="voice-speed" hint={hints.speed || '0.5 to 2. Empty: 1.'}>
                    <input id="voice-speed" className="form-input mono" value={form.speed} inputMode="decimal"
                           onChange={(e) => update('speed', e.target.value)} />
                  </Field>
                )}
              </div>
            </section>
          )}

          {form.kind !== 's2s' && (
            <section aria-labelledby="voice-form-pricing" className="mt-4">
              <h2 id="voice-form-pricing" className="section-title">
                Pricing <span className="text-sm text-muted fw-400">EUR per second of audio</span>
              </h2>
              <Field label="Price per second" htmlFor="voice-price"
                     hint={form.kind === 's2t'
                       ? 'Each call costs the seconds of audio it transcribes times this, charged to the user\'s budget like an LLM call. Empty: 0.'
                       : 'Each call costs the seconds of audio it speaks times this, charged to the user\'s budget like an LLM call. Empty: 0.'}>
                <input id="voice-price" className="form-input mono" type="number" step="any" min="0"
                       value={form.price_per_second} placeholder="0.00"
                       onChange={(e) => update('price_per_second', e.target.value)} />
              </Field>
            </section>
          )}

          <section aria-labelledby="voice-form-availability" className="mt-4">
            <h2 id="voice-form-availability" className="section-title">Availability</h2>
            <Switch checked={form.active} onChange={(v) => update('active', v)} label="Active"
                    description="Only active models can be picked on the Tools page." />
          </section>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner-sm" /> Saving…</> : (editingKey ? 'Save voice model' : 'Add voice model')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={!!testModel} onClose={() => setTestKey(null)} maxWidth={640}
             title={testModel ? `Test ${testModel.ui_name}` : ''}
             description={testModel?.kind === 's2t'
               ? 'Sends one of the samples to this model and shows the transcript.'
               : 'Sends the text to this model and plays the audio that came back.'}>
        {testModel && (
          <>
            <dl className="kv mb-4">
              <dt>Provider</dt>
              <dd>{testModel.provider_label}</dd>
              <dt>Endpoint</dt>
              <dd className="mono">{testModel.endpoint || 'Built in'}</dd>
              {testModel.model_id && <dt>Model ID</dt>}
              {testModel.model_id && <dd className="mono">{testModel.model_id}</dd>}
              {(testModel.languages || []).length > 0 && <dt>Speaks for the agent</dt>}
              {(testModel.languages || []).length > 0 && (
                <dd><span className="mono">{testModel.languages.join(', ')}</span>, on the Tools page</dd>
              )}
            </dl>
            <form onSubmit={(e) => { e.preventDefault(); if (!testState.loading) runTest(testModel); }}>
              {testModel.kind === 's2t' ? (
                samples.length ? (
                  <SamplePicker samples={samples} value={testState.sample}
                                onChange={(id) => setTest(testModel, { sample: id })} />
                ) : <p className="text-sm text-muted">The samples did not load.</p>
              ) : (
                <>
                  {testAccount?.error && (
                    <div className="notice notice-warn mb-3" role="status">
                      <Icon name="alertCircle" />
                      <div className="notice-body">
                        The account&apos;s voices could not be listed: {testAccount.error}
                      </div>
                    </div>
                  )}
                  {testAccount && !testAccount.voices.length ? (
                    // Nothing listed (a key not allowed to read voices, say): a voice ID typed in.
                    <Field label="Voice ID" htmlFor="voice-test-voice"
                           hint="A voice ID of the ElevenLabs account. Empty: the voice the Tools page gives this model.">
                      <input id="voice-test-voice" className="form-input mono" value={testState.voice}
                             spellCheck={false} autoComplete="off"
                             onChange={(e) => changeTestVoice(testModel, e.target.value.trim())} />
                    </Field>
                  ) : (
                    <Field label="Voice" htmlFor="voice-test-voice"
                           hint={testAccount
                             ? "The account's voices, each speaking any language. The default is the voice the Tools page gives this model first, else the account's first."
                             : 'Each voice speaks the language in brackets. The default is the voice the Tools page gives this model first, else its Italian one.'}>
                      <select id="voice-test-voice" className="form-input" value={testState.voice}
                              onChange={(e) => changeTestVoice(testModel, e.target.value)}>
                        <option value="">Default</option>
                        {voiceGroups(testVoices(testModel)).map(([name, group]) => (
                          <optgroup key={name} label={name}>
                            {group.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                          </optgroup>
                        ))}
                      </select>
                    </Field>
                  )}
                  <Field label="Text to synthesise" htmlFor="voice-test-text" hint="At most 1,000 characters.">
                    <textarea id="voice-test-text" className="form-input" rows={3} maxLength={1000}
                              value={testState.text} onChange={(e) => setTest(testModel, { text: e.target.value })} />
                  </Field>
                </>
              )}
              <div className="row gap-2 mt-3">
                <button type="submit" className="btn btn-primary" disabled={testState.loading}>
                  {testState.loading
                    ? <><span className="spinner-sm" /> Testing…</>
                    : <><Icon name="play" /> Run test</>}
                </button>
              </div>
            </form>
            <div className="mt-4" aria-live="polite">
              {testState.result && <VoiceTestResult result={testState.result} />}
            </div>
          </>
        )}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setTestKey(null)}>Close</button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default Voice;
